/**
 * Exportación a Excel (.xlsx) de las matrices de Avance y Venta/Indirectos.
 *
 * El helper es genérico: recibe las columnas (Año › Mes › Semana, ya aplanadas a
 * hojas) y una lista de filas jerárquicas (proyecto/bloque/obra/total). Escribe
 * los VALORES NUMÉRICOS CRUDOS (no el texto "₡1 234"), así Excel puede sumar,
 * filtrar y graficar. El formato de número se aplica vía numFmt de la celda.
 *
 * exceljs se importa de forma DINÁMICA dentro de exportarMatrizExcel para que la
 * librería (≈1 MB) no entre al bundle inicial; solo se descarga al exportar.
 */
import { colorArgbEstado } from '@/lib/coloresEstado';

/** Columna de datos = una hoja del árbol Año › Mes › Semana. */
export interface ColumnaExport {
  key: string;
  anio: number;
  mes: number;
  /** null = columna de mes; número = semana ISO. */
  semanaIso: number | null;
}

/** Una fila de la matriz, con su nivel de indentación y sus valores por columna. */
export interface FilaExport {
  /** 0 = proyecto, 1 = bloque, 2 = obra, 3 = gran total, 4 = subtotal por estado. */
  nivel: 0 | 1 | 2 | 3 | 4;
  etiqueta: string;
  /** Segunda línea opcional (display name de la obra). */
  sublabel?: string | null;
  /** colKey → valor numérico (o null si la celda está vacía). */
  valores: Map<string, number | null>;
  /**
   * colKey → estado de venta de esa celda. Si se pasa (y hay estado), la celda se
   * pinta con el color TENUE del estado — como el "Color por estado" de la vista.
   */
  estados?: Map<string, string | null>;
  /**
   * Color ARGB para pintar TODA la fila (subtotales por estado). Tiene prioridad
   * sobre `estados`.
   */
  colorFila?: string;
}

const NOMBRES_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/** Formato de número de Excel según el tipo de métrica. */
export type FormatoExport = 'moneda' | 'porcentaje' | 'numero';

const NUM_FMT: Record<FormatoExport, string> = {
  // ₡ con separador de miles, sin decimales (coincide con la vista).
  moneda: '"₡"#,##0',
  // % con 1 decimal; el valor crudo es fracción (0–1).
  porcentaje: '0.0%',
  // m² u otros, 1 decimal.
  numero: '#,##0.0',
};

export interface OpcionesExport {
  nombreArchivo: string;
  tituloHoja: string;
  /** Encabezado de la primera columna (jerarquía). */
  encabezadoJerarquia: string;
  columnas: ColumnaExport[];
  filas: FilaExport[];
  formato: FormatoExport;
}

/** Etiqueta legible de una columna (p.ej. "May" o "May S20"). */
function etiquetaColumna(c: ColumnaExport): string {
  const mes = NOMBRES_MES[c.mes - 1] ?? String(c.mes);
  return c.semanaIso != null ? `${mes} S${c.semanaIso}` : mes;
}

/** Genera y dispara la descarga del .xlsx en el navegador. */
export async function exportarMatrizExcel(opts: OpcionesExport): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Adelante BI Obras';
  wb.created = new Date();
  const ws = wb.addWorksheet(opts.tituloHoja, {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }],
  });

  // --- Fila 1: año (agrupado con merge) sobre las columnas de ese año ---
  // --- Fila 2: etiqueta de mes/semana ---
  const totalCols = opts.columnas.length + 1; // +1 por la columna de jerarquía
  ws.getCell(1, 1).value = opts.tituloHoja;
  ws.getCell(2, 1).value = opts.encabezadoJerarquia;

  // Agrupar columnas por año para el merge de la fila 1.
  let idx = 2; // las columnas de datos empiezan en la col 2 (1 = jerarquía)
  const aniosVistos = new Map<number, { ini: number; fin: number }>();
  for (const c of opts.columnas) {
    const r = aniosVistos.get(c.anio);
    if (r) r.fin = idx;
    else aniosVistos.set(c.anio, { ini: idx, fin: idx });
    ws.getCell(2, idx).value = etiquetaColumna(c);
    idx++;
  }
  for (const [anio, { ini, fin }] of aniosVistos) {
    if (fin > ini) ws.mergeCells(1, ini, 1, fin);
    ws.getCell(1, ini).value = anio;
    ws.getCell(1, ini).alignment = { horizontal: 'center' };
  }

  // Estilo de encabezado (filas 1 y 2).
  for (let r = 1; r <= 2; r++) {
    for (let c = 1; c <= totalCols; c++) {
      const cell = ws.getCell(r, c);
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F6B3B' } };
      cell.alignment = { horizontal: c === 1 ? 'left' : 'center', vertical: 'middle' };
    }
  }

  // --- Filas de datos ---
  const numFmt = NUM_FMT[opts.formato];
  for (const fila of opts.filas) {
    const fileRow = ws.addRow([]);
    // Columna de jerarquía: indentación por nivel.
    const sangria = '    '.repeat(fila.nivel);
    const etiqueta = fila.sublabel
      ? `${sangria}${fila.etiqueta} — ${fila.sublabel}`
      : `${sangria}${fila.etiqueta}`;
    const celdaJerarquia = fileRow.getCell(1);
    celdaJerarquia.value = etiqueta;
    if (fila.nivel <= 1 || fila.nivel === 3) celdaJerarquia.font = { bold: true };

    opts.columnas.forEach((c, i) => {
      const v = fila.valores.get(c.key);
      const cell = fileRow.getCell(i + 2);
      // Solo escribimos número si hay valor real (no 0/null) — así las celdas
      // vacías quedan en blanco, igual que la vista.
      if (v != null && v !== 0) {
        cell.value = v;
        cell.numFmt = numFmt;
      }
      cell.alignment = { horizontal: 'right' };
      // Color por estado de venta de ESA celda (fondo tenue), como la vista.
      const argbCelda = fila.colorFila ?? colorArgbEstado(fila.estados?.get(c.key));
      if (argbCelda) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argbCelda } };
      }
    });

    // Etiqueta de la fila de subtotal por estado también con su color.
    if (fila.colorFila) {
      celdaJerarquia.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: fila.colorFila },
      };
    }

    // Sombreado suave para grupos (proyecto/bloque) y total.
    if (fila.nivel === 0 || fila.nivel === 3) {
      for (let c = 1; c <= totalCols; c++) {
        fileRow.getCell(c).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fila.nivel === 3 ? 'FFE5E7EB' : 'FFD1FADF' },
        };
      }
    }
  }

  // Ancho de columnas.
  ws.getColumn(1).width = 40;
  for (let c = 2; c <= totalCols; c++) ws.getColumn(c).width = 13;

  // Descargar.
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = opts.nombreArchivo.endsWith('.xlsx')
    ? opts.nombreArchivo
    : `${opts.nombreArchivo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
